<template>
  <LcLotteryEditorCard
    ref="card"
    v-bind="{ complete, remaining, removable, fields, numberClass }"
    v-model:expanded="isExpanded"
    :style="[extraTheme]"
    :[EDITOR_PLAY_ID_ATTRIBUTE]="play.id"
    :clear-disabled="!clearEnabled"
    :done-label="$t('numberlottery.remainingnumbers.done')"
    :quick-pick-label="$t('numberlottery.playslip.button.quickpick')"
    :clear-label="$t('numberlottery.playslip.button.clear')"
    radius="medium"
    :warning="warning"
    :remove-aria-label="$t('editor.card.remove.arialabel')"
    @clear="handleClear"
    @remove="closePlay(play.id)"
    @quick-pick="handleQuickpick"
    @collapsed="notifyCollapsed(play.id)"
    @expanded="scrollExpandedPlayIntoView(cardRef)"
    @update:expanded="handleExpandedChange"
    separated
  >
    <template #headline="{ countClass }">
      <EditorRemainingHeadline
        v-bind="{ countClass }"
        keypath="numberlottery.remainingnumbers"
        :name="$t('numberlottery.remainingnumbers.name.base', visualRemaining)"
        :remaining="visualRemaining"
      />
    </template>

    <ul :class="{ 'number-lottery-picker-locked': isAnimating(play.id) }">
      <li
        v-for="{ name, selectableMax, selectableNumbers } in config.numbers"
        :key="name"
        ref="fieldItems"
      >
        <EditorNumberLotteryPickerField
          v-bind="{ name, lotteryId, selectableMax, selectableNumbers, play, config }"
          @complete="scrollToNextIncompleteField(config, play)"
        />
      </li>
    </ul>
  </LcLotteryEditorCard>
</template>

<script lang="ts" setup>
import type { LcLotteryNumbersProps } from '@lottocom/frontend-components'
import type { ComponentPublicInstance } from 'vue'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { EDITOR_PLAY_ID_ATTRIBUTE } from '../constants/editor'
import { NUMBER_LOTTERY_FIELD_NAME } from '../constants/numberLottery'
import { EDITOR_QUICK_PICK_MOTION } from '../constants/quickPickAnimation'
import { type EditorPlay, useNumberLotteryStore } from '../stores/numberLottery'
import { buildCardFields } from '../utils/editorFields'

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
}

const BASE_FIELD_INDEX = 0

const { config, lotteryId, play } = defineProps<Props>()

const { t } = useI18n()
const store = useNumberLotteryStore(lotteryId)
const closePlay = useEditorClosePlay(store)
const { extraBallBg, extraBallColor, extraHitIcon } = useNumberLotteryTheme(lotteryId)
const { cancel, getNumberState, getSummaryPlay, isAnimating, notifyCollapsed, triggerSinglePlay } =
  useEditorQuickPickAnimation()

const { scrollExpandedPlayIntoView, scrollExpandingPlayIntoView } = useExpandableScroll()
const cardRef = useTemplateRef<ComponentPublicInstance>('card')
const fieldRefs = useTemplateRef<HTMLElement[]>('fieldItems')
const { scrollToNextIncompleteField } = useNumberLotteryFieldScroll(fieldRefs, cardRef)

const clearEnabled = computed(() => !store.isPlayEmpty(play))

const extraTheme = computed(() => ({
  '--lottery-numbers-extra-bg': extraBallBg.value,
  '--lottery-numbers-extra-color': extraBallColor.value,
}))

const complete = computed(() => store.isFieldComplete(config, play, BASE_FIELD_INDEX))

const isExpanded = computed({
  get: () => store.expandedPlayId === play.id,
  set: (expanded) => store.setExpandedPlay(expanded ? play.id : null),
})

const warning = computed(() =>
  !isExpanded.value && store.isPlayFlagged(config, play)
    ? t('editor.play.badge.incomplete')
    : undefined,
)

const fields = computed(() =>
  buildCardFields(config, getSummaryPlay(play), (field) => {
    const showIcon =
      field?.name === NUMBER_LOTTERY_FIELD_NAME.Extra && store.isExtraHitEnabled(play)
    return { icon: showIcon ? extraHitIcon.value : undefined }
  }),
)

const remaining = computed(() =>
  store.remainingInField(play, BASE_FIELD_INDEX, config.numbers[BASE_FIELD_INDEX].selectableMax),
)

const removable = computed(
  () => isExpanded.value && store.isPlayEmpty(play) && store.plays.length > 1,
)

const visualRemaining = useEditorVisualRemaining(() => remaining.value)

const numberClass: LcLotteryNumbersProps['numberClass'] = (value, name) => {
  const state = getNumberState(play.id, value, name)
  return state ? `number-lottery-ball-${state}` : ''
}

const handleClear = () => {
  if (!clearEnabled.value || isAnimating(play.id)) return
  store.clearPlay(play.id)
}

const handleExpandedChange = (expanded: boolean) => {
  cancel(play.id)
  if (!expanded) return
  scrollExpandingPlayIntoView(cardRef.value)
}

const handleQuickpick = () => {
  triggerSinglePlay({
    config,
    ignoredFields: store.getIgnoredFieldNames(config, play),
    kind: 'grid',
    play,
    quickPick: () => store.quickPick(play.id, config),
    replaceAll: store.isPlayComplete(config, play),
  })
}

const quickPickMotion = EDITOR_QUICK_PICK_MOTION
</script>

<style lang="scss" scoped>
$roll-tick: v-bind("quickPickMotion.tick + 'ms'");
$settle-duration: v-bind("quickPickMotion.settle + 'ms'");

.number-lottery-picker-locked {
  pointer-events: none;

  /* stylelint-disable selector-class-pattern */
  :deep(.lc-lottery-number-selector__number) {
    &:not(
      .lc-lottery-number-selector__number--checked,
      .lc-lottery-number-selector__number--highlighted
    ) {
      opacity: 0.5;
    }
  }
  /* stylelint-enable selector-class-pattern */
}

:deep(.number-lottery-ball-rolling) {
  overflow: hidden;

  /* stylelint-disable-next-line selector-class-pattern */
  .lc-lottery-numbers__value {
    animation: number-lottery-ball-roll $roll-tick linear infinite;
  }
}

:deep(.number-lottery-ball-locked) {
  animation: number-lottery-ball-settle $settle-duration $transition-ease-pop;
}

@keyframes number-lottery-ball-roll {
  from {
    opacity: 0.35;
    transform: translateY(-55%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes number-lottery-ball-settle {
  from {
    transform: scale(1.07);
  }

  to {
    transform: scale(1);
  }
}
</style>
